import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceDot, ReferenceLine, ReferenceArea } from "recharts";
import { Panel } from "@/components/common";
import { usePoll } from "@/hooks/usePoll";
import { fmt, ldnTime } from "@/lib/api";

const TT = { background: "#0F172A", border: "1px solid #1E293B", fontSize: 12 };
const AX = { fill: "#64748B", fontSize: 10 };

function ZChart({ bars, k, zNow }) {
  const zs = bars.map((b) => b.z).filter((v) => v != null);
  const lim = Math.ceil(Math.max(k + 1, ...zs.map(Math.abs)));
  return (
    <div className="mt-2" data-testid="z-score-chart">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
        <span className="uppercase tracking-[0.16em]">z-score</span>
        <span className="num">Now <span className={zNow > 0 ? "text-rose-300" : zNow < 0 ? "text-emerald-300" : "text-slate-300"} data-testid="z-score-now">{zNow == null ? "-" : fmt(zNow)}</span>, bands at ±{fmt(k, 1)}</span>
      </div>
      <div className="h-40">
        <ResponsiveContainer>
          <ComposedChart data={bars} syncId="oil">
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="label" tick={AX} minTickGap={50} />
            <YAxis domain={[-lim, lim]} tick={AX} width={55} allowDataOverflow />
            <Tooltip contentStyle={TT} formatter={(v) => [fmt(v), "z"]} />
            <ReferenceArea y1={k} y2={lim} fill="#F43F5E" fillOpacity={0.08} />
            <ReferenceArea y1={-lim} y2={-k} fill="#10B981" fillOpacity={0.08} />
            <ReferenceLine y={k} stroke="#F43F5E" strokeDasharray="4 4" label={{ value: "Short zone", fill: "#FB7185", fontSize: 10, position: "insideTopLeft" }} />
            <ReferenceLine y={-k} stroke="#10B981" strokeDasharray="4 4" label={{ value: "Long zone", fill: "#34D399", fontSize: 10, position: "insideBottomLeft" }} />
            <ReferenceLine y={0} stroke="#F59E0B" strokeDasharray="3 3" />
            <Line dataKey="z" name="z" stroke="#A5B4FC" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function SpreadChart({ k = 2, zNow = null }) {
  const { data } = usePoll("/oil/chart?bars_n=150", 30000);
  const bars = (data?.bars || []).map((b) => ({ ...b, label: ldnTime(b.t), band: b.upper != null ? [b.lower, b.upper] : null }));
  const nearest = (iso) => {
    if (!bars.length || !iso) return null;
    const t = new Date(iso).getTime();
    return bars.reduce((a, b) => (Math.abs(new Date(b.t).getTime() - t) < Math.abs(new Date(a.t).getTime() - t) ? b : a), bars[0]);
  };
  return (
    <Panel title="Spread chart (Brent minus WTI) with z-score" testid="spread-chart">
      {bars.length < 2 ? (
        <p className="text-sm text-slate-500">The spread and z-score charts fill in as spread samples are recorded from MT5.</p>
      ) : (
        <>
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={bars} syncId="oil">
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={AX} minTickGap={50} hide />
                <YAxis domain={["auto", "auto"]} tick={AX} width={55} tickFormatter={(v) => fmt(v, 2)} />
                <Tooltip contentStyle={TT} formatter={(v, n) => [Array.isArray(v) ? `${fmt(v[0], 3)} to ${fmt(v[1], 3)}` : fmt(v, 3), n]} />
                <Area dataKey="band" name="band" stroke="none" fill="#6366F1" fillOpacity={0.09} isAnimationActive={false} />
                <Line dataKey="upper" name="upper" stroke="#F43F5E" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line dataKey="lower" name="lower" stroke="#10B981" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line dataKey="sma" name="SMA" stroke="#F59E0B" strokeDasharray="3 3" dot={false} isAnimationActive={false} />
                <Line dataKey="spread" name="spread" stroke="#38BDF8" strokeWidth={2} dot={false} isAnimationActive={false} />
                {(data?.trades || []).map((t, i) => {
                  const b = nearest(t.opened_at);
                  return b ? <ReferenceDot key={i} x={b.label} y={t.entry_spread} r={4} fill={t.side === "long" ? "#10B981" : "#F43F5E"} stroke="#0B0F17" /> : null;
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <ZChart bars={bars} k={k} zNow={zNow} />
        </>
      )}
    </Panel>
  );
}
